import React, { useEffect } from "react";
import { Button, Divider, Form, Space, message } from "antd";
import { getBusiness } from "../../../../features/reducers/business";
import ContentFormSubtitle from "../../../../common/ContentFormSubtitle";
import ContentPageTitle from "../../../../common/ContentPageTitle";
import LoadingForm from "../../../../common/LoadingForm";
import BusinessHours from "../../../../common/FormInputs/BusinessHours";
import { useDashboardFormData } from "../../../../hooks/useDashboardFormData";

export default function RestaurantWorkingHours(props) {
  const { business } = props;

  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const [
    fields,
    setFieldData,
    isSubmitting,
    submitForm,
    onFinishFailed,
    loadingForm,
  ] = useDashboardFormData(business);

  useEffect(() => {
    if (business.id !== undefined) {
      setFieldData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business]);

  const dayConfigs = [
    {
      label: "Sunday",
      key: "sunday",
    },
    {
      label: "Monday",
      key: "monday",
    },
    {
      label: "Tuesday",
      key: "tuesday",
    },
    {
      label: "Wednesday",
      key: "wednesday",
    },
    {
      label: "Thursday",
      key: "thursday",
    },
    {
      label: "Friday",
      key: "friday",
    },
    {
      label: "Saturday",
      key: "saturday",
    },
  ];

  const getDayFields = (dayKey) => ({
    available: `meta_is_available_${dayKey}`,
    open: `meta_open_time_${dayKey}`,
    close: `meta_close_time_${dayKey}`,
  });

  const copyScheduleFromDay = (sourceDay, targetDays) => {
    const sourceFields = getDayFields(sourceDay);
    const sourceAvailable = form.getFieldValue(sourceFields.available);
    const sourceOpen = form.getFieldValue(sourceFields.open);
    const sourceClose = form.getFieldValue(sourceFields.close);

    if (!sourceAvailable || !sourceOpen || !sourceClose) {
      messageApi.warning("Set Monday hours first to use this shortcut");
      return;
    }

    const valuesToSet = {};

    targetDays.forEach((dayKey) => {
      const targetFields = getDayFields(dayKey);
      valuesToSet[targetFields.available] = true;
      valuesToSet[targetFields.open] = sourceOpen;
      valuesToSet[targetFields.close] = sourceClose;
    });

    form.setFieldsValue(valuesToSet);
    messageApi.success("Hours copied successfully");
  };

  return (
    <div id="restaurant-working-hours-page">
      {contextHolder}

      <ContentPageTitle title="Restaurant Working Hours" icon="clock" />

      <div className="content-box">
        {loadingForm ? (
          <LoadingForm />
        ) : (
          <Form
            form={form}
            layout="vertical"
            autoComplete="off"
            initialValues={fields}
            onFinishFailed={onFinishFailed}
            onFinish={(values) => {
              values.name = business.name;

              submitForm(
                values,
                `/api/business/${business.id}`,
                getBusiness(business.id),
              );
            }}
            disabled={isSubmitting}
          >
            <ContentFormSubtitle
              title="Working Hours"
              subtitle="Configure the days and opening hours your restaurant is available."
            />

            <Space wrap style={{ marginBottom: 16 }}>
              <Button
                onClick={() =>
                  copyScheduleFromDay("monday", [
                    "tuesday",
                    "wednesday",
                    "thursday",
                    "friday",
                  ])
                }
              >
                Copy Monday to Weekdays
              </Button>

              <Button
                onClick={() =>
                  copyScheduleFromDay("monday", [
                    "sunday",
                    "tuesday",
                    "wednesday",
                    "thursday",
                    "friday",
                    "saturday",
                  ])
                }
              >
                Copy Monday to All Days
              </Button>
            </Space>

            {dayConfigs.map((dayConfig, index) => {
              const previousDay = index > 0 ? dayConfigs[index - 1] : null;

              return (
                <BusinessHours
                  key={dayConfig.key}
                  label={dayConfig.label}
                  checkboxFieldName={`meta_is_available_${dayConfig.key}`}
                  openTimeFieldName={`meta_open_time_${dayConfig.key}`}
                  closeTimeFieldName={`meta_close_time_${dayConfig.key}`}
                  previousDayLabel={previousDay ? previousDay.label : undefined}
                  previousDayOpenFieldName={
                    previousDay
                      ? `meta_open_time_${previousDay.key}`
                      : undefined
                  }
                  previousDayCloseFieldName={
                    previousDay
                      ? `meta_close_time_${previousDay.key}`
                      : undefined
                  }
                  form={form}
                />
              );
            })}

            <Divider />

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={isSubmitting}>
                {isSubmitting ? "Saving" : "Save Working Hours"}
              </Button>
            </Form.Item>
          </Form>
        )}
      </div>
    </div>
  );
}
